import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15000,
});

// Request interceptor — attach JWT
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor — handle 401 refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;

    if (error.response?.status === 401 && !original._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          original.headers.Authorization = `Bearer ${token}`;
          return api(original);
        });
      }

      original._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        const { data } = await axios.post(`${API_URL}/auth/refresh-token`, { refreshToken });
        localStorage.setItem('accessToken', data.data.accessToken);
        localStorage.setItem('refreshToken', data.data.refreshToken);
        processQueue(null, data.data.accessToken);
        original.headers.Authorization = `Bearer ${data.data.accessToken}`;
        return api(original);
      } catch (err) {
        processQueue(err);
        localStorage.clear();
        window.location.href = '/login';
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;

// ─── Service modules ─────────────────────────────────

export const authService = {
  login: (credentials) => api.post('/auth/login', credentials),
  googleLogin: (credential) => api.post('/auth/google', { credential }),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
  changePassword: (data) => api.put('/auth/change-password', data),
  refreshToken: (refreshToken) => api.post('/auth/refresh-token', { refreshToken }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPasswordByToken: (token, newPassword) => api.post('/auth/reset-password-token', { token, newPassword }),
};

export const leadService = {
  getAll: (params) => api.get('/leads', { params }),
  getById: (id) => api.get(`/leads/${id}`),
  getProgress: (id) => api.get(`/leads/${id}/progress`),
  create: (data) => api.post('/leads', data),
  update: (id, data) => api.put(`/leads/${id}`, data),
  updateStatus: (id, data) => api.patch(`/leads/${id}/status`, data),
  assign: (id, data) => api.patch(`/leads/${id}/assign`, data),
  bulkAssign: (data) => api.post('/leads/bulk-assign', data),
  recordCall: (id, data) => api.post(`/leads/${id}/call`, data),
  addNote: (id, data) => api.post(`/leads/${id}/notes`, data),
  delete: (id) => api.delete(`/leads/${id}`),
  globalSearch: (params) => api.get('/leads/search', { params }),
  connect: (id, channel, templateId) => api.post(`/leads/${id}/connect`, { channel, ...(templateId && { templateId }) }),
  markRegistered: (data) => api.post('/leads/lead/registered', data),
  markAdmissionCompleted: (data) => api.post('/leads/lead/admission-completed', data),
};

export const userService = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  toggleStatus: (id) => api.patch(`/users/${id}/toggle-status`),
  toggleLock: (id) => api.patch(`/users/${id}/toggle-lock`),
  updateCourses: (id, courseIds) => api.patch(`/users/${id}/courses`, { courseIds }),
  delete: (id) => api.delete(`/users/${id}`),
  getCounsellors: (params) => api.get('/users/counsellors', { params }),
  getLoginHistory: (id, params) => api.get(`/users/${id}/login-history`, { params }),
};

export const roleService = {
  getAll: () => api.get('/roles'),
  getById: (id) => api.get(`/roles/${id}`),
  create: (data) => api.post('/roles', data),
  update: (id, data) => api.put(`/roles/${id}`, data),
  delete: (id) => api.delete(`/roles/${id}`),
  clone: (id, data) => api.post(`/roles/${id}/clone`, data),
  updatePermissions: (id, data) => api.put(`/roles/${id}/permissions`, data),
  updateMenus: (id, data) => api.put(`/roles/${id}/menus`, data),
};

export const menuService = {
  getAll: () => api.get('/menus'),
  create: (data) => api.post('/menus', data),
  update: (id, data) => api.put(`/menus/${id}`, data),
};

export const integrationService = {
  getStatus: () => api.get('/integrations/status'),
  getLogs: (params) => api.get('/integrations/logs', { params }),
  getMappings: () => api.get('/integrations/mappings'),
  createMapping: (data) => api.post('/integrations/mappings', data),
  updateMapping: (id, data) => api.put(`/integrations/mappings/${id}`, data),
  deleteMapping: (id) => api.delete(`/integrations/mappings/${id}`),
  getMyOUSyncMappings: () => api.get('/integrations/my-sync'),
  toggleMyOUSyncMapping: (id) => api.put(`/integrations/my-sync/${id}/toggle`),
};

export const courseService = {
  getAll: (params) => api.get('/courses', { params }),
  getById: (id) => api.get(`/courses/${id}`),
  create: (data) => api.post('/courses', data),
  update: (id, data) => api.put(`/courses/${id}`, data),
  delete: (id) => api.delete(`/courses/${id}`),
  sync: () => api.post('/courses/sync'),
};

export const masterService = {
  getLeadSources: () => api.get('/masters/lead-sources'),
  createLeadSource: (data) => api.post('/masters/lead-sources', data),
  updateLeadSource: (id, data) => api.put(`/masters/lead-sources/${id}`, data),
  deleteLeadSource: (id) => api.delete(`/masters/lead-sources/${id}`),

  getLeadStatuses: () => api.get('/masters/lead-statuses'),
  createLeadStatus: (data) => api.post('/masters/lead-statuses', data),
  updateLeadStatus: (id, data) => api.put(`/masters/lead-statuses/${id}`, data),
  deleteLeadStatus: (id) => api.delete(`/masters/lead-statuses/${id}`),

  getCallFeedbacks: () => api.get('/masters/call-feedbacks'),
  createCallFeedback: (data) => api.post('/masters/call-feedbacks', data),
  updateCallFeedback: (id, data) => api.put(`/masters/call-feedbacks/${id}`, data),
  deleteCallFeedback: (id) => api.delete(`/masters/call-feedbacks/${id}`),

  getRetailOUs: () => api.get('/masters/retail-ous'),
  createRetailOU: (data) => api.post('/masters/retail-ous', data),
  updateRetailOU: (id, data) => api.put(`/masters/retail-ous/${id}`, data),
  deleteRetailOU: (id) => api.delete(`/masters/retail-ous/${id}`),
};

export const dashboardService = {
  getSuperAdmin: (params) => api.get('/dashboard/super-admin', { params }),
  getRetailOU: (params) => api.get('/dashboard/retail-ou', { params }),
  getCounsellor: (params) => api.get('/dashboard/counsellor', { params }),
  getCounsellorPerformance: (id, params) => api.get(`/dashboard/counsellor-performance/${id}`, { params }),
  getCounsellorOverview: (params) => api.get('/dashboard/counsellors-overview', { params }),
};

export const followUpService = {
  getAll: (params) => api.get('/followups', { params }),
  getLeadsView: (params) => api.get('/followups/leads-view', { params }),
  getToday: () => api.get('/followups/today'),
  getStats: () => api.get('/followups/stats'),
  create: (data) => api.post('/followups', data),
  complete: (id, data) => api.patch(`/followups/${id}/complete`, data),
  reschedule: (id, data) => api.patch(`/followups/${id}/reschedule`, data),
  cancel: (id) => api.patch(`/followups/${id}/cancel`),
};

export const templateService = {
  getAll: (params) => api.get('/templates', { params }),
  getOne: (id) => api.get(`/templates/${id}`),
  create: (data) => api.post('/templates', data),
  update: (id, data) => api.put(`/templates/${id}`, data),
  remove: (id) => api.delete(`/templates/${id}`),
  preview: (templateId, leadId) => api.post('/templates/preview', { templateId, leadId }),
};

export const bulkMessageService = {
  start: (leadIds, channel, templateId) => api.post('/bulk-messages', { leadIds, channel, templateId }),
  getCampaign: (id) => api.get(`/bulk-messages/${id}`),
  getCampaignItems: (id, params) => api.get(`/bulk-messages/${id}/items`, { params }),
  list: (params) => api.get('/bulk-messages', { params }),
};

export const reportService = {
  courseWise: (params) => api.get('/reports/course-wise', { params }),
  counsellorWise: (params) => api.get('/reports/counsellor-wise', { params }),
  leadSource: (params) => api.get('/reports/lead-source', { params }),
  calls: (params) => api.get('/reports/calls', { params }),
  overall: (params) => api.get('/reports/overall', { params }),
};

export const notificationService = {
  getAll: (params) => api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllAsRead: () => api.patch('/notifications/mark-all-read'),
};

export const csvService = {
  getTemplate: () => api.get('/csv/template', { responseType: 'blob' }),
  validate: (data) => api.post('/csv/validate', data),
  importCSV: (data) => api.post('/csv/import', data),
  getHistory: (params) => api.get('/csv/history', { params }),
  rollback: (id) => api.post(`/csv/${id}/rollback`),
};

export const auditService = {
  getAll: (params) => api.get('/audit-logs', { params }),
  getStats: () => api.get('/audit-logs/stats'),
};

export const activityService = {
  getAll: (params) => api.get('/activity-logs', { params }),
};
